<!--
  @component ProviderSummary
  @description UC-REG-217 — Tab "Por prestador" de Cuenta Médica. Resumen
    agregado del período: por cada prestador cuántas prestaciones registró,
    cuántas se liquidaron, cuántas quedan por liquidar, cuántos lotes envió,
    y los montos aceptado/debitado/facturado. Fila total al pie.

  @example <ProviderSummary />
-->
<template>
  <div class="prov-sum">
    <div class="prov-sum__toolbar">
      <el-select
        v-model="filterMonth"
        placeholder="Todos los meses"
        clearable
        size="small"
        style="width: 150px"
        @change="load"
      >
        <el-option
          v-for="m in months"
          :key="m.value"
          :label="m.label"
          :value="m.value"
        />
      </el-select>
      <el-select
        v-model="filterYear"
        placeholder="Año"
        size="small"
        style="width: 100px"
        @change="load"
      >
        <el-option v-for="y in years" :key="y" :label="y" :value="y" />
      </el-select>
      <div class="prov-sum__spacer" />
      <el-button size="small" icon="el-icon-download" @click="exportExcel">
        Exportar Excel
      </el-button>
    </div>

    <!-- KPIs globales -->
    <div class="prov-sum__kpis">
      <div class="kpi">
        <div class="kpi__label">Prestaciones registradas</div>
        <div class="kpi__value">{{ totals.registradas || 0 }}</div>
      </div>
      <div class="kpi">
        <div class="kpi__label">Liquidadas</div>
        <div class="kpi__value">{{ totals.liquidadas || 0 }}</div>
        <div class="kpi__sub">{{ totals.aLiquidar || 0 }} aún por liquidar</div>
      </div>
      <div class="kpi">
        <div class="kpi__label">Valor aceptado</div>
        <div class="kpi__value money">
          {{ formatMoney(totals.valorAceptado) }}
        </div>
      </div>
      <div class="kpi">
        <div class="kpi__label">Total facturado</div>
        <div class="kpi__value money">{{ formatMoney(totals.facturado) }}</div>
      </div>
    </div>

    <!-- Tabla por prestador -->
    <div class="prov-sum__card" v-loading="isLoading">
      <table class="prov-sum__table">
        <thead>
          <tr>
            <th>Prestador</th>
            <th class="num">Registradas</th>
            <th class="num">Liquidadas</th>
            <th class="num">A liquidar</th>
            <th class="num">Liq. enviadas</th>
            <th class="num">Valor aceptado</th>
            <th class="num">Debitado</th>
            <th class="num">Facturado</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!isLoading && rows.length === 0">
            <td class="empty" colspan="8">Sin actividad en el período</td>
          </tr>
          <tr
            v-for="r in rows"
            :key="r.providerId"
            class="clickable"
            @click="goToTransactions(r)"
          >
            <td>
              <span class="prcode">{{ r.providerCode || "—" }}</span>
              <span class="pname">{{ r.providerName }}</span>
            </td>
            <td class="num">{{ r.registradas }}</td>
            <td class="num">{{ r.liquidadas }}</td>
            <td class="num">{{ r.aLiquidar }}</td>
            <td class="num">{{ r.liquidacionesEnviadas }}</td>
            <td class="num">{{ formatMoney(r.valorAceptado) }}</td>
            <td class="num" :class="{ 'num--err': r.debitado > 0 }">
              {{ r.debitado > 0 ? formatMoney(r.debitado) : "—" }}
            </td>
            <td class="num">
              {{ r.facturado > 0 ? formatMoney(r.facturado) : "—" }}
            </td>
          </tr>
        </tbody>
        <tfoot v-if="rows.length > 0">
          <tr class="total-row">
            <td>TOTAL</td>
            <td class="num">{{ totals.registradas }}</td>
            <td class="num">{{ totals.liquidadas }}</td>
            <td class="num">{{ totals.aLiquidar }}</td>
            <td class="num">{{ totals.liquidacionesEnviadas }}</td>
            <td class="num">{{ formatMoney(totals.valorAceptado) }}</td>
            <td class="num">{{ formatMoney(totals.debitado) }}</td>
            <td class="num">{{ formatMoney(totals.facturado) }}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import serviceRecordService from "@/services/service-record";

const MONTHS = [
  { value: 1, label: "Enero" },
  { value: 2, label: "Febrero" },
  { value: 3, label: "Marzo" },
  { value: 4, label: "Abril" },
  { value: 5, label: "Mayo" },
  { value: 6, label: "Junio" },
  { value: 7, label: "Julio" },
  { value: 8, label: "Agosto" },
  { value: 9, label: "Septiembre" },
  { value: 10, label: "Octubre" },
  { value: 11, label: "Noviembre" },
  { value: 12, label: "Diciembre" },
];

export default {
  name: "ProviderSummary",

  data() {
    return {
      rows: [],
      totals: {},
      isLoading: false,
      filterMonth: new Date().getMonth() + 1,
      filterYear: new Date().getFullYear(),
      months: MONTHS,
      years: [2024, 2025, 2026, 2027],
    };
  },

  created() {
    this.load();
  },

  methods: {
    /** @description Carga el resumen del período */
    async load() {
      this.isLoading = true;
      try {
        const params = { year: this.filterYear };
        if (this.filterMonth) params.month = this.filterMonth;
        const res = await serviceRecordService.summaryByProvider(params);
        this.rows = res.rows || [];
        this.totals = res.totals || {};
      } catch (e) {
        this.$message.error("Error al cargar el resumen por prestador");
      } finally {
        this.isLoading = false;
      }
    },

    /** @param {number} n */
    formatMoney(n) {
      if (n == null || n === 0) return "$0";
      return `$${Number(n).toLocaleString("es-AR")}`;
    },

    /**
     * @description Click en fila → va a Transacciones filtrado por prestador.
     * @param {Object} r
     */
    goToTransactions(r) {
      this.$router.push({
        name: "service-record-list",
        query: { provider: r.providerId },
      });
    },

    /** @description Exporta la tabla a Excel */
    exportExcel() {
      const headers = [
        "Nº prestador",
        "Prestador",
        "Registradas",
        "Liquidadas",
        "A liquidar",
        "Liquidaciones enviadas",
        "Valor aceptado",
        "Debitado",
        "Facturado",
      ];
      const data = this.rows.map((r) => [
        r.providerCode || "",
        r.providerName,
        r.registradas,
        r.liquidadas,
        r.aLiquidar,
        r.liquidacionesEnviadas,
        r.valorAceptado,
        r.debitado,
        r.facturado,
      ]);
      const aoa = [headers, ...data];
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Por prestador");
      const monthLabel = this.filterMonth
        ? MONTHS[this.filterMonth - 1].label
        : "todos";
      XLSX.writeFile(
        wb,
        `resumen_prestadores_${monthLabel}_${this.filterYear}.xlsx`
      );
    },
  },
};
</script>

<style scoped>
.prov-sum {
  font-family: "Plus Jakarta Sans", sans-serif;
  font-feature-settings: "zero" 0;
}
.prov-sum__toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
}
.prov-sum__spacer {
  flex: 1;
}

.prov-sum__kpis {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin-bottom: 20px;
}
.kpi {
  background: #fff;
  border: 1px solid #e6eaf1;
  border-radius: 12px;
  padding: 16px 18px;
}
.kpi__label {
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #9aa3b5;
  font-weight: 600;
}
.kpi__value {
  font-size: 24px;
  font-weight: 700;
  color: #1a1f36;
  margin-top: 6px;
  font-variant-numeric: tabular-nums;
}
.kpi__value.money {
  color: #087443;
}
.kpi__sub {
  font-size: 11px;
  color: #9aa3b5;
  margin-top: 2px;
}

.prov-sum__card {
  background: #fff;
  border: 1px solid #e6eaf1;
  border-radius: 14px;
  overflow: hidden;
}
.prov-sum__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.prov-sum__table thead th {
  text-align: left;
  padding: 13px 16px;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #9aa3b5;
  font-weight: 600;
  background: #fafbfd;
  border-bottom: 1px solid #e6eaf1;
  white-space: nowrap;
}
.prov-sum__table th.num,
.prov-sum__table td.num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.prov-sum__table tbody td {
  padding: 13px 16px;
  border-bottom: 1px solid #eef1f6;
  color: #1a1f36;
}
.prov-sum__table tbody tr.clickable {
  cursor: pointer;
  transition: background 120ms;
}
.prov-sum__table tbody tr.clickable:hover {
  background: #eef2ff;
}
.prov-sum__table td.empty {
  text-align: center;
  color: #9aa3b5;
  padding: 40px;
}
.prov-sum__table td.num--err {
  color: #b3261e;
}
.prcode {
  font-weight: 600;
  color: #1a1f36;
  margin-right: 8px;
}
.pname {
  color: #5b6478;
  font-size: 12.5px;
}
.total-row td {
  padding: 13px 16px;
  border-top: 2px solid #cdd4de;
  font-weight: 700;
  color: #1a1f36;
}
</style>
